import React, {useContext, useEffect, useState} from 'react';
import {FormattedMessage} from "react-intl";

// Local Components
import MobileView from "./components/MobileView";
import TakeoutShipmentPopup from "./components/TakeoutShipmentPopup";
import DesktopView from "./components/DesktopView";
import SuccessToast from "../components/SuccessToast";

// API
import {list} from 'api/shipment/list';
import {getPlaces} from 'api/wms/wmsPlaces/list';
import {list as cancellationReasonList} from 'api/cancellationReason/list'

// Context
import {WMSContext} from "../../context/WMSContext";
import {ClickAndCollectListContext} from "./context/ClickAndCollectListContext";

// Utils
import {UserAgentUtils} from "utils/useragent";
import {LineItemStatus} from "utils/LineItemStatus";
import {ShipmentStatus} from "../../../../utils/ShipmentStatus";
import {getWMSContainersWithType} from "../../../../api/wms/wmsContainers/wmsContainers";
import {WmsContainerType} from "../../../../utils/WMSContainerType";
import {WmsItemsStatus} from "../../../../utils/WmsItemsStatus";
import {LineItemTransitions} from "../../../../utils/lineItemTransitions";

function ClickAndCollectList(props) {
    const {currentInventory} = useContext(WMSContext)

    const [shipments, setShipments] = React.useState([]);
    const [containers, setContainers] = useState()

    const [response, setResponse] = React.useState(null);

    const [totalPages, setTotalPages] = React.useState(0);
    const [itemsPerPage] = React.useState(10);

    const [actualLink, setActualLink] = React.useState(null);
    const [previousLink, setPreviousLink] = React.useState(null);
    const [nextLink, setNextLink] = React.useState(null);

    const [searchString, setSearchString] = React.useState(null);

    const [selectedShipment, setSelectedShipment] = React.useState(null);
    const [selectedZone, setSelectedZone] = React.useState(null);
    const [selectedContainer, setSelectedContainer] = useState(null)
    const [cancellationReasons, setCancellationReasons] = React.useState([]);

    const [openToast, setOpenToast] = React.useState(false);

    const [sort, setSort] = React.useState({ orderBy: 'date', orderDirection: 'asc' });

    const title = "wms.cc.list.title"
    const subtitle = "wms.cc.list.subtitle"

    // equivalent to componentDidMount
    useEffect(() => {
        if (!currentInventory) {
            return;
        }
        getShipments();
        getWMSContainersWithType(WmsContainerType.CC, currentInventory.id).then((response) => setContainers(response))
        cancellationReasonList(`transition=${LineItemTransitions.CANCEL}`).then((response) => {
            setCancellationReasons(response);
        })
    }, [currentInventory])

    useEffect(() => {
        if (!response) {
            return;
        }

        setShipments(response["hydra:member"]);

        setTotalPages(Math.ceil(response['hydra:totalItems'] / itemsPerPage));

        setActualLink(response['hydra:view']?.['@id'].replace('/shipments?', ''));
        setPreviousLink(response['hydra:view']?.['hydra:previous']?.replace('/shipments?', ''));
        setNextLink(response['hydra:view']?.['hydra:next']?.replace('/shipments?', ''));
    }, [response]);

    useEffect(() => {
        if (!currentInventory || searchString === null) return;

        const handler = setTimeout(() => {
            getShipments(createLink(1, searchString));
        }, 1000);

        return () => clearTimeout(handler);
    }, [searchString, currentInventory, selectedContainer, sort]);

    function createLink(page = 1, searchString = '') {
        // Shipments must have CC type containers, CC WMSItems (status = click_collect_ready and container is CC type)
        //  and lineItem in to-ship or confirmed
        const params = {
            inventoryId: currentInventory.id,
            'groups[]': 'wms_shipment:read',
            pagination: true,
            itemsPerPage: itemsPerPage,
            page: page,
            lineItemStatus: [LineItemStatus.TO_SHIP, LineItemStatus.CONFIRMED],
            status: ShipmentStatus.READY_TO_BE_SHIPPED,
            shipmentContainerType: [WmsContainerType.CC],
        }

        // Shipments can be with or without wmsItems (specialOrders), that's why we don't check for the presence of wmsItems.

        if (searchString) {
            params.search = encodeURIComponent(searchString);
        }

        if (selectedContainer) {
            params['wmsContainer'] = selectedContainer;
        }

        switch (sort.orderBy) {
            case 'date':
                params['order[createdAt]'] = sort.orderDirection;
                break;
            case 'wmsContainer':
                params['order[container.name]'] = sort.orderDirection;
                break;
            case 'name':
                params['order[order.customerShippingLastname]'] = sort.orderDirection;
                break;
            default:
                break;
        }

        return new URLSearchParams(params).toString();
    }

    function getShipments(link = createLink()) {
        list(link).then((res) => {
            setResponse(res);
        })
    }

    function onPageChange(page) {
        getShipments(createLink(page));
    }

    function refreshShipments() {
        getShipments(actualLink);
    }

    function handleRequestSort(property)  {
        setSort((prev) => {
            if (prev.orderBy === property) {
                // Inverse la direction si on clique sur la même colonne
                return {
                    orderBy: property,
                    orderDirection: prev.orderDirection === 'asc' ? 'desc' : 'asc',
                };
            } else {
                // Nouvelle colonne commence par 'desc'
                return {
                    orderBy: property,
                    orderDirection: 'desc',
                };
            }
        });
    }

    return (
        <ClickAndCollectListContext.Provider value={{
            title,
            subtitle,
            shipments,
            selectedShipment,
            setSelectedShipment,
            selectedZone,
            setSelectedZone,
            containers,
            selectedContainer,
            setSelectedContainer,
            searchString,
            setSearchString,
            onPageChange,
            getShipments,
            refreshShipments,
            nextLink,
            previousLink,
            totalPages,
            sort,
            setSort,
            openToast: () => setOpenToast(true),
            handleRequestSort
        }}>
            {UserAgentUtils.isMobile() ? (
                <MobileView />
            ) : (
                <DesktopView />
            )}
            {!!selectedShipment && (
                <TakeoutShipmentPopup cancellationReasons={cancellationReasons} />
            )}
            <SuccessToast
                title={<FormattedMessage id='wms.cc.list.toast.title' />}
                isOpen={openToast}
                handleClose={() => setOpenToast(false)}
            />
        </ClickAndCollectListContext.Provider>
    )
}

export default ClickAndCollectList;
